import { AppBar, alpha, Box, Toolbar } from '@mui/material'
import React, { type ComponentProps } from 'react'
import SVGLogoWhite from '@/assets/svg/logo.white.svg?react'
import { IMPERSONATION_BANNER_HEIGHT } from '@/components/ImpersonationBanner/ImpersonationBanner'
import { LinkRouter } from '@/helpers/compositions/LinkRouter'
import { useChromeCollapsed } from '@/hooks/useChromeCollapse'
import { useViewDomRect } from '@/hooks/useViewDomRect'
import { SpotlightInput } from '@/modules/Spotlight/Spotlight.input'
import { useAuthContext } from '@/providers/Global'
import { hasBaseRole } from '@/utils/authorities'

import { NavbarGlobalEntity } from './Navbar.globalEntity'
import { NavbarGlobalShareholder } from './Navbar.globalShareholder'
import { NavbarReportButton } from './Navbar.reportButton'
import { NavbarUser } from './Navbar.user'

//	///////////////////////
//	MARK: TYPES
export interface ICNavbarProps
  extends Partial<Omit<ComponentProps<typeof AppBar>, 'position' | 'ref'>> {}

//	///////////////////////
//	MARK: EXPORTS
const NavbarInternal: React.FC<ICNavbarProps> = (props) => {
  const Auth = useAuthContext()
  const baseRole = hasBaseRole(Auth.active?.roles)
  const [ref] = useViewDomRect('navbar')

  const chromeCollapsed = useChromeCollapsed()

  const isImpersonating = Boolean(Auth.active?.impersonation)

  return (
    <AppBar
      {...props}
      ref={ref}
      position='fixed'
      elevation={0}
      sx={{
        // `visibility` et non `display` : la barre est en position fixe (elle ne prend
        // aucune place dans le flux) et doit rester mesurée, sinon la sous-navbar se
        // recale sur une hauteur de navbar nulle et réapparaît en deux temps.
        visibility: chromeCollapsed ? 'hidden' : undefined,
        top: isImpersonating ? `${IMPERSONATION_BANNER_HEIGHT}px` : 0,
        bgcolor: 'primary.main',
        color: 'common.white',
        borderBottom: (theme) => `1px solid ${alpha(theme.palette.common.black, 0.22)}`,
        backgroundImage: (theme) =>
          `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.04)} 0%, transparent 100%)`,
        boxShadow: (theme) =>
          `0 1px 0 ${alpha(theme.palette.common.white, 0.06)} inset, 0 2px 12px ${alpha(
            theme.palette.common.black,
            0.18,
          )}`,
      }}
    >
      <Toolbar
        sx={{
          gap: { xs: 1, md: 2 },
          minHeight: { xs: 56, md: 64 },
          px: { xs: 1.5, md: 2.5 },
        }}
      >
        <LinkRouter
          to='/'
          sx={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            mr: { xs: 0.5, md: 1 },
            transition: 'opacity 150ms ease',
            '&:hover': { opacity: 0.85 },
          }}
        >
          <SVGLogoWhite
            style={{
              height: '2rem',
              verticalAlign: 'middle',
            }}
          />
        </LinkRouter>
        {baseRole ? <SpotlightInput /> : null}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, md: 1 },
            flexShrink: 0,
          }}
        >
          {baseRole ? <NavbarReportButton /> : null}
          {Auth.active!.globalEntity && <NavbarGlobalEntity />}
          <NavbarGlobalShareholder />
          <NavbarUser />
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export const Navbar = React.memo(NavbarInternal) as typeof NavbarInternal
